import { i, s, x } from '../../lit-element.js';
import '../../fs-heading.js';
import { n, s as safeDefineFsComponent } from '../../query-assigned-elements.js';
import '../../index2.js';

/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */function t(t){return n({...t,state:!0})}

const style = i`
  :host {
    --title-width: 147px;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .title-skeleton {
    width: var(--title-width, 147px);
  }
`;

var loaderStyles$1 = i`.fs-skeleton-loader{background:var(--fs-color-digital-gray15,hsl(0,0%,83%));overflow:hidden;position:relative}.fs-skeleton-loader.fs-skeleton-loader--text-large{border-radius:var(--fs-radius-pill,100vh);height:var(--fs-size-small2,12px);width:var(--fs-size-xlarge5,96px)}.fs-skeleton-loader.fs-skeleton-loader--text-small{border-radius:var(--fs-radius-pill,100vh);height:var(--fs-size-small1,8px);width:var(--fs-size-xlarge1,64px)}.fs-skeleton-loader.fs-skeleton-loader--circle{aspect-ratio:var(--fs-aspect-ratio1-x1,1);border-radius:var(--fs-radius-circle,50%);width:calc(var(--fs-size-medium2, 24px) + var(--fs-size-xlarge5, 96px))}.fs-skeleton-loader.fs-skeleton-loader--rectangle{height:calc(var(--fs-size-medium2, 24px) + var(--fs-size-xlarge5, 96px));width:calc(var(--fs-size-xlarge1, 64px) + var(--fs-size-xlarge5, 96px))}.fs-skeleton-loader::after{animation:fs-skeleton-swipe calc(var(--fs-animation-duration5, 0.5s) * 2.5) var(--fs-animation-easing-purposeful-standard, cubic-bezier(0.30, 0.00, 0.00, 1.00)) infinite;background:linear-gradient(99.23deg,rgba(255,255,255,0) 15.59%,rgba(255,255,255,0.5) 50%,rgba(255,255,255,0) 84.41%);background-repeat:no-repeat;content:"";display:block;height:100%;position:absolute;width:100%}@keyframes fs-skeleton-swipe{from{transform:translateX(-100%)}to{transform:translateX(100%)}}`;

var __decorate$2 = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * `FsSkeletonLoader` implements the Financial Services implementation of the skeleton
 * loader component component.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-skeleton-loader-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-skeleton-loader>` call
 *
 * ```javascript
 * import '@cof/fs-skeleton-loader-wc/fs-skeleton-loader.js';
 * ```
 *
 * To import the `FsSkeletonLoader` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsSkeletonLoader } from '@cof/fs-skeleton-loader-wc';
 * ```
 *
 * When using the skeleton loader in isolation (without the loader scope) take care to have a visually-hidden
 * loading label. See core accessibility guidelines for more information.
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <!-- Will show loader until the image is loaded -->
 * <fs-skeleton-loader
 *   active
 *   toggle-on-load
 *   variant="rectangle | circle | text-large | text-small"
 * >
 *  <img src="path/to/image.png" alt="Alt text" height="100" width="100">
 * </fs-skeleton-loader>
 *
 * <!-- Default text loader -->
 * <fs-skeleton-loader
 *   ?active="${active}"
 *   variant="text-large"
 * >
 *   <h3 class="fs-heading-text fs-heading-text--medium">
 *     Optional text that will toggle on on load. Either add text here or remove the loader entirely.
 *   </h3>
 * </fs-skelelton-loader>
 * ```
 *
 * @element fs-skeleton-loader
 * @slot - The default slot will only render when the element active is false. Each loader should have only one element child.
 */
class FsSkeletonLoader extends s {
    constructor() {
        super(...arguments);
        /**
         * When set to true the skeleton loader will be active
         * and the slotted content will be hidden
         */
        this.active = false;
        /**
         * When set to true the element will toggle its active state off
         * whenever a nested image dispatches a load event. This will also
         * set active to true if and when the img's src attribute is changed.
         */
        this.toggleOnLoad = false;
        /** Describes the type of loader */
        this.variant = 'rectangle';
        /**
         * If we know the element is an image and has dimensions, adjust the
         * loader to ensure it has the same size
         */
        this._isImage = false;
        /**
         * Triggered when an img.src changes to set the loader's active state to true
         * this will prevent the wrong image from being displayed when an image is updated
         */
        this._observer = new MutationObserver(() => {
            this.active = true;
        });
        /** MutationObserver config, we only need to watch the src attribute */
        this._observerConfig = { attributes: true, attributeFilter: ['src'] };
        this._onImageLoad = () => {
            this.active = false;
        };
    }
    static get styles() {
        return [
            i `
        :host { display: flex; }
        .fs-skeleton-loader { flex: 1 1 auto; }
        .fs-skeleton-loader--image { height: 100% !important; }
      `,
            loaderStyles$1
        ];
    }
    /** Remove event listeners and disconnect observer */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._loadTarget) {
            this._loadTarget.removeEventListener('load', this._onImageLoad);
        }
        this._observer.disconnect();
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return x `
      <div class="fs-skeleton-loader fs-skeleton-loader--${this.variant} ${this._isImage ? 'fs-skeleton-loader--image' : ''}" ?hidden="${!this.active}"></div>
      <slot @slotchange="${this._onSlotchange}" ?hidden="${this.active}"></slot>
    `;
    }
    /** React to active state changes */
    updated(changes) {
        if (changes.has('active')) {
            this.setAttribute('aria-hidden', this.active.toString());
        }
    }
    /** Fired when the slot's content changes, manipulates internal state if toggleOnLoad is true */
    _onSlotchange({ target }) {
        /** Do nothing unless explicitly opted in */
        if (!this.toggleOnLoad) {
            return;
        }
        /** Loop over assigned elements to check for images */
        target.assignedElements().forEach((element) => {
            /** If the loadTarget changes, we need to remove the load event listener */
            if (this._loadTarget && element !== this._loadTarget) {
                this._loadTarget.removeEventListener('load', this._onImageLoad);
            }
            /** If the slotted element is an img, add listeners and observe changes */
            if (element.localName === 'img' || element.localName === 'iframe') {
                this.active = true;
                element.addEventListener('load', this._onImageLoad);
                this._loadTarget = element;
                this._observer.observe(element, this._observerConfig);
                /** If possible, make sure the host has the same size as the slotted image */
                if (element.height && element.width) {
                    this.style.height = `${element.height}px`;
                    this.style.width = `${element.width}px`;
                    this._isImage = true;
                }
            }
        });
    }
}
/** The element's canonical name for use with safe define */
FsSkeletonLoader.canonicalName = 'skeleton-loader';
__decorate$2([
    n({ type: Boolean, reflect: true })
], FsSkeletonLoader.prototype, "active", void 0);
__decorate$2([
    n({ type: Boolean, attribute: 'toggle-on-load' })
], FsSkeletonLoader.prototype, "toggleOnLoad", void 0);
__decorate$2([
    n({ type: String, reflect: true })
], FsSkeletonLoader.prototype, "variant", void 0);
__decorate$2([
    t()
], FsSkeletonLoader.prototype, "_isImage", void 0);

safeDefineFsComponent.define(FsSkeletonLoader);

var loaderStyles = i`:host{clip:rect(1px,1px,1px,1px);height:1px;overflow:hidden;position:absolute;top:auto;white-space:nowrap;width:1px}`;

var __decorate$1 = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * `FsLoadingIndicator` is a utility component intended to be used in conjunction with any of the FS
 * loaders or the FsSkeletonLoader component. This component is used to allow assistive technologies
 * to be aware that a given scope is in a loading state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-linear-loader-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-linear-loader>` call
 *
 * ```javascript
 * import '@cof/fs-linear-loader-wc/fs-linear-loader.js';
 * ```
 *
 * To import the `FsLoadingIndicator` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsLoadingIndicator } from '@cof/fs-linear-loader-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <fs-loading-indicator ?active="${active}">
 *   Your custom loading message
 * </fs-loading-indicator>
 * ```
 *
 * @element fs-loading-indicator
 * @slot - The default slot is the loading message. Defaults to "Loading".
 */
class FsLoadingIndicator extends s {
    constructor() {
        super(...arguments);
        /** When true, the visually-hidden message will be present */
        this.active = false;
    }
    static get styles() {
        return [loaderStyles];
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return x `
      ${this.active ? x `<slot>Loading</slot>` : ''}
    `;
    }
}
/** The element's canonical name */
FsLoadingIndicator.canonicalName = 'loading-indicator';
__decorate$1([
    n({ type: Boolean, reflect: true })
], FsLoadingIndicator.prototype, "active", void 0);

var __decorate = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** Scoped definition of the loading indicator */
if (!customElements.get('fs-loading-indicator-for-loader-scope')) {
    customElements.define('fs-loading-indicator-for-loader-scope', FsLoadingIndicator);
}
/**
 * `FsSkeletonLoaderScope` is a utility component which wraps several instances
 * of `FsSkeletonLoader` and toggles the states simultaneously.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-skeleton-loader-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-skeleton-loader-scope>` call
 *
 * ```javascript
 * import '@cof/fs-skeleton-loader-wc/fs-skeleton-loader-scope.js';
 * ```
 *
 * To import the `FsSkeletonLoaderScope` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsSkeletonLoaderScope } from '@cof/fs-skeleton-loader-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <fs-skeleton-loader-scope ?active="${active}">
 * <fs-skeleton-loader toggle-on-load>
 *   <img
 *     alt="Bill Murray"
 *     height="300"
 *     width="300"
 *     .src="${active ? '' : 'https://www.fillmurray.com/300/300'}"
 *    >
 *    </fs-skeleton-loader>
 *    <fs-skeleton-loader variant="text-large">
 *       <h2 class="fs-heading-text fs-heading-text--medium">Lorem ipsum</h2>
 *     </fs-skeleton-loader>
 *     <fs-skeleton-loader variant="text-small">
 *       <p class="fs-body-text fs-body-text--small">Dolor sit amet consequetetor lancet.</p>
 *     </fs-skeleton-loader>
 *     <fs-skeleton-loader variant="text-small">
 *       <p class="fs-body-text fs-body-text--small">Adelispig neurot don lorem ipsum.</p>
 *     </fs-skeleton-loader>
 *   </fs-skeleton-loader-scope>
 * ```
 *
 * @element fs-skeleton-loader-scope
 * @slot - The default slot is the content of the loader. Expects one or more instances of FsSkeletonLoader
 */
class FsSkeletonLoaderScope extends s {
    constructor() {
        super(...arguments);
        /**
         * When set to true the skeleton loader will be active
         * and the slotted content will be hidden
         */
        this.active = false;
        /** A custom loading message */
        this.loadingMessage = 'Loading';
    }
    static get styles() {
        return [
            i `
        :host { display: block; }
      `
        ];
    }
    get loaders() {
        return Array.from(this.children).filter((child) => {
            return child instanceof FsSkeletonLoader;
        });
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return x `
      <slot></slot>
      <fs-loading-indicator-for-loader-scope ?active="${this.active}">
        ${this.loadingMessage}
      </fs-loading-indicator-for-loader-scope>`;
    }
    /** React to active state changes */
    updated(changes) {
        if (changes.has('active')) {
            this.loaders.forEach((loader) => {
                if (this.active === true) {
                    loader.active = true;
                }
                else if (this.active === false && !loader.toggleOnLoad) {
                    loader.active = false;
                }
            });
        }
    }
}
/** The element's canonical name for use with safe define */
FsSkeletonLoaderScope.canonicalName = 'skeleton-loader-scope';
__decorate([
    n({ type: Boolean, reflect: true })
], FsSkeletonLoaderScope.prototype, "active", void 0);
__decorate([
    n({ type: String, attribute: 'loading-message', reflect: false })
], FsSkeletonLoaderScope.prototype, "loadingMessage", void 0);

safeDefineFsComponent.define(FsSkeletonLoaderScope);

class RefiSkeletonLoader extends s {
  constructor() {
    super();
    this.active = false;
    this.titleWidth = '147px';
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--title-width', this.titleWidth);
  }

  static styles = [style];

  static properties = {
    active: { type: Boolean, attribute: 'active', reflect: true },
    titleWidth: { type: String, attribute: 'title-width' },
  };

  render() {
    return x` <div class="wrapper">
      <fs-skeleton-loader-scope class="wrapper" ?active=${this.active}>
        <fs-skeleton-loader class="title-skeleton" variant="text-large"></fs-skeleton-loader>
        <fs-skeleton-loader class="content-skeleton" variant="text-small"></fs-skeleton-loader>
        <fs-skeleton-loader class="content-skeleton" variant="text-small"></fs-skeleton-loader>
      </fs-skeleton-loader-scope>
    </div>`;
  }
}

export { RefiSkeletonLoader };
